import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IoAlertCircleOutline, IoCartOutline, IoEyeOutline, IoRocketOutline, IoTimeOutline, IoWarningOutline } from 'react-icons/io5';
import { notifications } from '@mantine/notifications';
import { CachedImage, Checkbox, DotMenu, DoubleCheckModal } from '@shared/ui';
import { useSetProductFlag, useDeleteProduct, useRemoveFromCategory } from '../../hooks/useProducts';
import { ProductFlagType } from '../../types/product-flag-type.enum';
import type { ProductCardResponse } from '../../types';
import { productThumbSrc } from '../../utils';
import ProductPrice from './ProductPrice';
// import ProductCategories from './ProductCategories';
import ProductBadges from './ProductBadges';
import ManageProductStocksModal from '../ManageProductStocksModal';
import PhotosBeforeShowModal from '../PhotosBeforeShowModal';
import { usePickupPoints } from '@modules/pickup-points';
import styles from './ProductCard.module.css';

interface ProductCardProps {
  product: ProductCardResponse;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (productId: number, selected: boolean) => void;
  hideMenu?: boolean;
  onEdit?: (productId: number) => void;
}

export default function ProductCard({
  product,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
  hideMenu = false,
  onEdit: onEditProp,
}: ProductCardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const setProductHidden = useSetProductFlag(ProductFlagType.HIDDEN);
  const { data: pickupPoints = [], isLoading: pickupPointsLoading } = usePickupPoints();
  const hasPickupPoints = pickupPoints.length > 0;
  const deleteProduct = useDeleteProduct();
  const removeFromCategory = useRemoveFromCategory();
  const [isDeleteCategoryModalOpen, setIsDeleteCategoryModalOpen] = useState(false);
  const [isDeleteProductModalOpen, setIsDeleteProductModalOpen] = useState(false);
  const [categoryToRemove, setCategoryToRemove] = useState<{ id: number; name: string } | null>(null);
  const [showStocksBeforeShowModal, setShowStocksBeforeShowModal] = useState(false);
  const [showPhotosBeforeShowModal, setShowPhotosBeforeShowModal] = useState(false);

  const firstImage = product.images && product.images.length > 0 ? product.images[0] : null;
  const hasModerationStatus = product.approved === null || product.approved === undefined || product.approved === false;

  // Преобразуем значения в числа, если они пришли как строки
  const price = typeof product.price === 'string' ? parseFloat(product.price) : product.price;
  const hasDiscount = !!(product.discount && product.discountedPrice);

  // Определяем, откуда переходим, и передаем текущий путь в state
  const linkState = { from: location.pathname };

  const handleEdit = () => {
    if (onEditProp) {
      onEditProp(product.id);
    } else {
      navigate(`/products/${product.id}/edit`, { state: linkState });
    }
  };

  const handleHide = async () => {
    if (product.isHidden && (!product.images || product.images.length === 0)) {
      setShowPhotosBeforeShowModal(true);
      return;
    }

    if (product.isHidden && product.isOutOfStock && product.sellerId != null) {
      setShowStocksBeforeShowModal(true);
      return;
    }

    try {
      await setProductHidden.mutateAsync({
        id: product.id,
        value: !product.isHidden,
      });

      notifications.show({
        title: 'Успешно',
        message: product.isHidden ? 'Товар успешно показан' : 'Товар успешно скрыт',
        color: 'green',
      });
    } catch (error) {
      console.error('Ошибка при скрытии/показе товара:', error);
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      notifications.show({
        title: 'Ошибка',
        message: msg ?? 'Ошибка при изменении видимости товара',
        color: 'red',
      });
    }
  };

  const handleStocksSavedBeforeShow = async () => {
    await setProductHidden.mutateAsync({
      id: product.id,
      value: false,
    });
    notifications.show({
      title: 'Успешно',
      message: 'Товар успешно показан',
      color: 'green',
    });
  };

  // const handleRemoveFromCategory = (categoryId: number, categoryName: string, e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   e.preventDefault();
  //   setCategoryToRemove({ id: categoryId, name: categoryName });
  //   setIsDeleteCategoryModalOpen(true);
  // };

  const confirmRemoveFromCategory = async () => {
    if (!categoryToRemove) return;

    try {
      await removeFromCategory.mutateAsync({
        productId: product.id,
        categoryId: categoryToRemove.id,
      });

      notifications.show({
        title: 'Успешно',
        message: 'Товар успешно удален из категории',
        color: 'green',
      });

      setIsDeleteCategoryModalOpen(false);
      setCategoryToRemove(null);
    } catch (error: any) {
      console.error('Ошибка при удалении товара из категории:', error);
      notifications.show({
        title: 'Ошибка',
        message: error?.response?.data?.message || 'Ошибка при удалении товара из категории',
        color: 'red',
      });
    }
  };

  const cancelRemoveFromCategory = () => {
    setIsDeleteCategoryModalOpen(false);
    setCategoryToRemove(null);
  };

  const handleDelete = () => {
    setIsDeleteProductModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteProduct.mutateAsync(product.id);
      notifications.show({
        title: 'Успешно',
        message: 'Товар успешно удален',
        color: 'green',
      });
      setIsDeleteProductModalOpen(false);
    } catch (error: unknown) {
      const responseData = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data
        : undefined;
      const errorMessage = responseData?.message ?? (error instanceof Error ? error.message : 'Неизвестная ошибка');
      notifications.show({
        title: 'Ошибка',
        message: errorMessage,
        color: 'red',
      });
      setIsDeleteProductModalOpen(false);
    }
  };

  const cancelDelete = () => {
    setIsDeleteProductModalOpen(false);
  };

  const cannotShowWithoutPickupPoints =
    product.isHidden && (pickupPointsLoading || !hasPickupPoints);

  const showZeroStockWarning = useMemo(() => {
    if (
      pickupPointsLoading ||
      !hasPickupPoints ||
      product.sellerId == null ||
      product.pickupStocks === undefined
    ) {
      return false;
    }
    const byPointId = new Map(
      product.pickupStocks.map((s) => [s.pickupPointId, s]),
    );
    return pickupPoints.some((pp) => {
      const row = byPointId.get(pp.id);
      const qty = row?.quantity ?? 0;
      const unlimited = Boolean(row?.unlimitedStock);
      return !unlimited && qty === 0;
    });
  }, [
    pickupPointsLoading,
    hasPickupPoints,
    product.sellerId,
    product.pickupStocks,
    pickupPoints,
  ]);

  const menuItems = [
    {
      label: 'Редактировать',
      onClick: handleEdit,
    },
    {
      label: product.isHidden ? 'Показать' : 'Скрыть',
      onClick: handleHide,
      disabled: cannotShowWithoutPickupPoints,
      title: cannotShowWithoutPickupPoints
        ? 'Сначала добавьте точку самовывоза'
        : undefined,
    },
    // {
    //   label: product.isOutOfStock ? 'В наличии' : 'Закончился',
    //   onClick: handleSetOutOfStock,
    // },
    // {
    //   label: 'Добавить в подборку',
    //   onClick: handleAddToSet,
    // },
    // {
    //   label: 'Добавить в категорию',
    //   onClick: handleAddToCategory,
    // },
    {
      label: 'Удалить',
      onClick: handleDelete,
      isDanger: true,
    },
  ];

  const cardContent = (
    <>
      <div className={styles.productCardMedia}>
        <div
          className={`${styles.productCardImage} ${product.isHidden || product.isOutOfStock || hasModerationStatus
              ? styles.productCardImageHidden
              : ''
            }`}
        >
          {firstImage ? (
            <CachedImage
              src={productThumbSrc(firstImage) || firstImage.url}
              alt={product.name}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className={styles.productCardImagePlaceholder} aria-hidden>
              Нет фото
            </div>
          )}
          {hasModerationStatus ? (
            <div className={styles.productCardBadges}>
              {(product.approved === null || product.approved === undefined) && (
                <span
                  className={`${styles.productCardBadge} ${styles.productCardModerationBadgePending}`}
                >
                  <IoTimeOutline size={12} aria-hidden />
                  На модерации
                </span>
              )}
              {product.approved === false && (
                <span
                  className={`${styles.productCardBadge} ${styles.productCardModerationBadgeRejected}`}
                >
                  <IoAlertCircleOutline size={12} aria-hidden />
                  Отклонён
                </span>
              )}
              {product.promotionEnabled ? (
                <span
                  className={`${styles.productCardBadge} ${styles.productCardBadgePromotion}`}
                >
                  <IoRocketOutline size={12} aria-hidden />
                  продвижение
                </span>
              ) : null}
            </div>
          ) : (
            <ProductBadges
              productId={product.id}
              hasDiscount={hasDiscount}
              isPopular={product.isPopular}
              isNew={product.isNew}
              isHidden={product.isHidden}
              isOutOfStock={product.isOutOfStock}
              promotionEnabled={Boolean(product.promotionEnabled)}
              customSets={product.customSets}
            />
          )}
          {showZeroStockWarning ? (
            <div className={styles.productCardStockWarning} role="status">
              <IoWarningOutline size={14} aria-hidden />
              <span>Остатки</span>
            </div>
          ) : null}
        </div>

        {(isSelectionMode || !hideMenu) && (
          <div className={styles.productCardMenu} onClick={(e) => e.stopPropagation()}>
            {isSelectionMode ? (
              <Checkbox
                checked={isSelected}
                onChange={(e) => onSelect?.(product.id, e.currentTarget.checked)}
              />
            ) : (
              <DotMenu items={menuItems} />
            )}
          </div>
        )}
      </div>

      <div className={styles.productCardContent}>
        {/* <ProductCategories
          categories={product.categories}
          onRemoveClick={handleRemoveFromCategory}
          isRemoving={removeFromCategory.isPending}
        /> */}

        <div className={styles.productCardMeta}>
          <span className={styles.productCardId}>#{product.id}</span>
          {product.rating != null ? (
            <span className={styles.productCardRating} title="Рейтинг">
              ★ {product.rating}
            </span>
          ) : null}
          <span className={styles.productCardStat} title="Куплено">
            <IoCartOutline size={13} aria-hidden />
            {product.purchaseCount ?? 0}
          </span>
          <span className={styles.productCardStat} title="Просмотрено">
            <IoEyeOutline size={13} aria-hidden />
            {product.viewedCount ?? 0}
          </span>
        </div>

        <h3 className={styles.productCardTitle}>{product.name}</h3>

        <div className={styles.productCardFooter}>
          <ProductPrice
            price={price}
            discount={product.discount}
            discountedPrice={product.discountedPrice}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className={styles.productCardWrapper}>
      {isSelectionMode ? (
        <div
          className={styles.productCard}
          onClick={() => onSelect?.(product.id, !isSelected)}
          style={{ cursor: 'pointer' }}
        >
          {cardContent}
        </div>
      ) : (
        <Link to={`/products/${product.id}`} state={linkState} className={styles.productCard}>
          {cardContent}
        </Link>
      )}

      {isDeleteCategoryModalOpen && <DoubleCheckModal
        isOpen={isDeleteCategoryModalOpen}
        onClose={cancelRemoveFromCategory}
        title="Удалить товар из категории?"
        message={categoryToRemove ? `Вы уверены, что хотите удалить товар из категории "${categoryToRemove.name}"?` : ''}
        secondButtonText="Удалить"
        secondButtonAction={confirmRemoveFromCategory}
        isLoading={removeFromCategory.isPending}
      />}

      {isDeleteProductModalOpen && <DoubleCheckModal
        isOpen={isDeleteProductModalOpen}
        onClose={cancelDelete}
        title="Удалить товар?"
        message={`Вы уверены, что хотите удалить товар "${product.name}"?`}
        secondButtonText="Удалить"
        secondButtonAction={confirmDelete}
        isLoading={deleteProduct.isPending}
      />}

      {product.sellerId != null && (
        <ManageProductStocksModal
          isOpen={showStocksBeforeShowModal}
          onClose={() => setShowStocksBeforeShowModal(false)}
          product={product}
          onSaved={handleStocksSavedBeforeShow}
          beforeShowHint
        />
      )}
      <PhotosBeforeShowModal
        isOpen={showPhotosBeforeShowModal}
        onClose={() => setShowPhotosBeforeShowModal(false)}
        onUpload={handleEdit}
      />
    </div>
  );
}

